import { CanAccess } from '@modules/common/components/CanAccess';
import { SelectMetadataDataType } from '@modules/common/components/SelectMetadataDataType';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import { formatNumToCompactNotation } from '@modules/common/utils';
import { ManageMetadataEditDrawerFooter } from '@modules/integrationsV2/components/ManageMetadataSection/ManageMetadataEditDrawerFooter';
import { STAR_METADATA_VISIBILITY_OFF_MESSAGE } from '@modules/integrationsV2/constants';
import { useFetchCoverageForMetadataField } from '@modules/integrationsV2/hooks';
import { useUpdateMetadata } from '@modules/integrationsV2/hooks/useUpdateMetadata';
import { useValidateEditMetadataInput } from '@modules/integrationsV2/hooks/useValidateEditMetadataInput';
import {
  EditMetadataInput,
  IntegrationSource,
  ManageMetadataTableRow,
} from '@modules/integrationsV2/types';
import {
  getDisplayNameForMetadataType,
  trackIntegrationPageManageMetadataEvent,
} from '@modules/integrationsV2/utils';
import { useFetchSampleMetadataValues } from '@modules/metadataManagement/hooks';
import { stopPropagationForHotKeysFromInput } from '@modules/taxonomy/helpers/stopEventPropagationForHotkeysFromInput';
import { EditRounded, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IntegrationType,
  MetadataDataType,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import pluralize from 'pluralize';
import { ReactNode, useEffect, useState } from 'react';

const SAMPLE_VALUES_LENGTH = 4;

interface ManageMetadataEditDrawerContentProps {
  metadataToEdit: ManageMetadataTableRow;
  onClose: () => void;
  integrationSource: IntegrationSource;
  shouldShowStarredField?: boolean;
}

export const ManageMetadataEditDrawerContent = ({
  metadataToEdit,
  onClose,
  integrationSource,
  shouldShowStarredField,
}: ManageMetadataEditDrawerContentProps) => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const defaultEditMetadataInput: EditMetadataInput = {
    key: metadataToEdit.key,
    source: metadataToEdit.source,
    namespace: metadataToEdit.namespace,
    displayText: metadataToEdit.displayText,
    isHidden: metadataToEdit.isHidden,
    isImportant: metadataToEdit.isImportant,
    description: metadataToEdit.description,
    type: metadataToEdit.type,
  };

  const [editMetadataInput, setEditMetadataInput] = useState<EditMetadataInput>(
    defaultEditMetadataInput,
  );

  const { values: metadataValues, isLoading: isFetchingSampleMetadataValues } =
    useFetchSampleMetadataValues({
      source: {
        name: metadataToEdit.source ?? 'COMMON',
        namespace: metadataToEdit.namespace,
      },
      key: metadataToEdit.key,
      sampleValuesLength: SAMPLE_VALUES_LENGTH,
    });

  const { value: fetchedCoverage, isLoading: isFetchingPercentageCoverage } =
    useFetchCoverageForMetadataField({
      metadataDetails: metadataToEdit,
      metadataIntegrationType: integrationSource.integrationType,
      shouldSkip: metadataToEdit.coverage !== undefined,
    });

  useEffect(() => {
    setEditMetadataInput(defaultEditMetadataInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataToEdit.key]);

  const [updateMetadata, { isLoading }] = useUpdateMetadata();

  const { hasError, errorFor } = useValidateEditMetadataInput({
    input: editMetadataInput,
    eqlSource: integrationSource.source ?? '',
    metadataToEdit,
  });

  const [isEditDataTypeOpen, setIsEditDataTypeOpen] = useState(false);

  const onSave = async () => {
    try {
      await updateMetadata(editMetadataInput);
      onClose();
      trackIntegrationPageManageMetadataEvent({
        eventName: 'Manage Metadata Drawer - Save Changes Button Clicked',
        integration: {
          channelType: integrationSource.channel.type,
          integrationType: integrationSource.integrationType,
          namespace: metadataToEdit.namespace,
        },
        updatedMetadata: editMetadataInput,
      });
    } catch {
      // do nothing, error is handled in the hook
    }
  };

  const onChangeMetadataDescription = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEditMetadataInput((input) => ({
      ...input,
      description: e.target.value,
    }));
  };

  const onChangeMetadataVisibility = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = e.target.checked;
    setEditMetadataInput((input) => ({
      ...input,
      isHidden: !isChecked,
    }));
    if (!isChecked) {
      setEditMetadataInput((input) => ({
        ...input,
        isImportant: false,
      }));
    }
  };

  const onChangeMetadataStarred = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setEditMetadataInput((input) => ({
      ...input,
      isImportant: isChecked,
    }));
  };

  const onChangeMetadataType = (value: MetadataDataType) => {
    setEditMetadataInput((input) => ({
      ...input,
      type: value,
    }));
  };

  const sampleMetadataValues = metadataValues?.map((value) => value) ?? [];

  const renderSampleMetadataValues = () => {
    if (isFetchingSampleMetadataValues) {
      return <CircularProgress size={20} />;
    }

    if (sampleMetadataValues.length === 0) {
      return (
        <Typography color="text.secondary">No sample values found.</Typography>
      );
    }

    return (
      <Stack
        direction="row"
        alignItems="center"
        rowGap={1.5}
        columnGap={2}
        flexWrap="wrap"
      >
        {sampleMetadataValues.map((sampleValue) => (
          <Tooltip arrow key={sampleValue} title={sampleValue} placement="top">
            <Chip label={sampleValue} variant="outlined" />
          </Tooltip>
        ))}
      </Stack>
    );
  };

  const renderFieldDetail = ({
    field,
    detail,
    isLoading = false,
  }: {
    field: ReactNode;
    detail: ReactNode;
    isLoading?: boolean;
  }) => {
    return (
      <Stack spacing={1.5}>
        <Typography>{field}</Typography>
        {isLoading ? (
          <Box>
            <Skeleton
              variant="rounded"
              width={30}
              height={16}
              sx={{
                display: 'inline-block',
                verticalAlign: 'middle',
                bgcolor: 'text.disabled',
              }}
            />
          </Box>
        ) : (
          <Typography color="text.secondary">{detail}</Typography>
        )}
      </Stack>
    );
  };

  const getTooltipTitleForCoverage = () => {
    const { integrationType, channel, namespace } = integrationSource;

    const integrationTypeLabel =
      integrationType === IntegrationType.Feedback
        ? 'feedback records'
        : pluralize(integrationType.toLowerCase());

    const sourceNameLabel = namespace
      ? `${namespace} - ${channel.displayName}`
      : channel.displayName;

    return (
      <Box>
        Percentage of {integrationTypeLabel} from{' '}
        <Box component="span" fontWeight={700}>
          {sourceNameLabel}
        </Box>{' '}
        that have a populated{' '}
        <Box component="span" fontWeight={700}>
          {metadataToEdit.displayText}
        </Box>{' '}
        value.
      </Box>
    );
  };

  return (
    <>
      <CanAccess
        resource={PermissionResource.Metadata}
        operation={PermissionOperation.Write}
        tooltipPlacement="top"
        tooltipFollowCursor
      >
        <Stack spacing={5} p={4}>
          <Stack spacing={4}>
            <Stack direction="row" alignItems="flex-start" spacing={12}>
              {renderFieldDetail({
                field: 'Original Name',
                detail: metadataToEdit.originalDisplayText,
              })}
              {renderFieldDetail({
                field: 'Data Type',
                detail: !isEditDataTypeOpen ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    pb={1.25}
                  >
                    <Box>
                      {getDisplayNameForMetadataType(
                        editMetadataInput.type ?? metadataToEdit.type,
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setIsEditDataTypeOpen(true)}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <Box width={150}>
                    <SelectMetadataDataType
                      isOpen
                      value={editMetadataInput.type}
                      onChange={onChangeMetadataType}
                      onClose={() => setIsEditDataTypeOpen(false)}
                      size="small"
                    />
                  </Box>
                ),
              })}
            </Stack>
            <TextField
              required
              label="Metadata Name"
              error={!!errorFor.displayText}
              helperText={errorFor.displayText}
              onKeyDown={stopPropagationForHotKeysFromInput}
              value={editMetadataInput.displayText}
              disabled={isLoading}
              onChange={(event) => {
                setEditMetadataInput((input) => ({
                  ...input,
                  displayText: event.target.value,
                }));
              }}
            />
          </Stack>
          <TextField
            value={editMetadataInput.description}
            onChange={onChangeMetadataDescription}
            label="Metadata Description"
            fullWidth
            variant="outlined"
            onKeyDown={stopPropagationForHotKeysFromInput}
          />
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {renderFieldDetail({
              field: 'Visible',
              detail: (
                <>
                  <Box>Shows the metadata on the platform.</Box>
                  <Box>
                    (In {isSearchToFeedEnabled ? 'Feed' : 'Search'}, Quantify,
                    Taxonomy etc.)
                  </Box>
                </>
              ),
            })}
            <Switch
              checked={
                editMetadataInput.isHidden !== undefined
                  ? !editMetadataInput.isHidden
                  : !metadataToEdit.isHidden
              }
              onChange={onChangeMetadataVisibility}
            />
          </Stack>
          {shouldShowStarredField && (
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              {renderFieldDetail({
                field: 'Starred',
                detail: (
                  <>
                    <Box>
                      {editMetadataInput.isHidden
                        ? STAR_METADATA_VISIBILITY_OFF_MESSAGE
                        : 'Starred Metadata is visible upfront on the feedback card (at the bottom)'}
                    </Box>
                    {errorFor.isImportant && (
                      <Box
                        sx={{
                          color: 'error.main',
                        }}
                      >
                        {errorFor.isImportant}
                      </Box>
                    )}
                  </>
                ),
              })}
              <Switch
                checked={
                  editMetadataInput.isImportant !== undefined
                    ? editMetadataInput.isImportant
                    : metadataToEdit.isImportant
                }
                onChange={onChangeMetadataStarred}
                disabled={editMetadataInput.isHidden}
              />
            </Stack>
          )}
          <Stack direction="row" alignItems="flex-start" spacing={12}>
            {renderFieldDetail({
              field: 'Unique Values',
              detail: formatNumToCompactNotation({
                value: metadataToEdit.cardinality,
                maximumFractionDigits: 1,
              }),
            })}
            {renderFieldDetail({
              field: (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box>Coverage</Box>
                  <Tooltip
                    arrow
                    placement="top"
                    title={getTooltipTitleForCoverage()}
                  >
                    <InfoOutlined
                      fontSize="small"
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                  </Tooltip>
                </Stack>
              ),
              detail: `${metadataToEdit.coverage ?? fetchedCoverage}%`,
              isLoading: isFetchingPercentageCoverage,
            })}
          </Stack>
          <Stack spacing={2}>
            <Typography>Sample Values</Typography>
            {renderSampleMetadataValues()}
          </Stack>
        </Stack>
      </CanAccess>
      <ManageMetadataEditDrawerFooter
        onCancel={onClose}
        onSave={onSave}
        isSaveRequestInProgress={isLoading}
        isSaveDisabled={hasError}
      />
    </>
  );
};
